import {
  type RefObject,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from "react";

import { SLOT_LOOK } from "./draft_fill";
import { drawnBlocks } from "./insert_gap";
import { type SetupSlot } from "./use_drafting_setup";

// Easy setup's preview of a kept section: a tinted block where its place will
// go, as Your sections draws a section not yet drafted (see SLOT_CLASS).
export const SETUP_SLOT_CLASS = "draft-setup-slot";
const SELECTED_CLASS = "draft-setup-slot-selected";
export const SETUP_SLOT_STYLE = `.${SETUP_SLOT_CLASS} { margin: 4px 0; ${SLOT_LOOK} cursor: pointer; } .${SELECTED_CLASS}, .${SETUP_SLOT_CLASS}:focus-visible { outline: 2px solid #8b5cf6; outline-offset: 1px; }`;

const isInView = (element: Element, view: Window) => {
  const { top, bottom } = element.getBoundingClientRect();
  return top >= 0 && bottom <= view.innerHeight;
};

// While his template is reviewed: a slot in the drawn Report before the block
// each kept section goes before (after the last, at the end), the selected one
// outlined and scrolled to, and a click on one (or Enter or Space) selects it. Slots are put in
// only when where they go changes; a rename or a selection changes them in place.
export const useSetupSlots = ({
  frameRef,
  root,
  slots,
  selected,
  onSelect,
}: {
  frameRef: RefObject<HTMLIFrameElement | null>;
  root: Element | null;
  slots: ReadonlyArray<SetupSlot>;
  selected: number | null;
  onSelect(block: number): void;
}) => {
  // Each block's first drawn piece, by its number in his file.
  const starts = useMemo(() => {
    const found = new Map<number, Element>();
    if (!root) return found;
    drawnBlocks(root).forEach(({ element, index, first }) => {
      if (first) found.set(index, element);
    });
    return found;
  }, [root]);
  const drawn = useRef(new Map<number, HTMLElement>());
  const slotsRef = useRef(slots);
  slotsRef.current = slots;
  const places = slots
    .map(({ block, before }) => `${String(block)}:${String(before)}`)
    .join(",");

  useLayoutEffect(() => {
    if (!root) return;
    const doc = root.ownerDocument;
    const last = [...starts.keys()].at(-1) ?? -1;
    const end = starts.get(last)?.parentElement?.lastElementChild;
    const placed = drawn.current;
    slotsRef.current.forEach(({ block, before }) => {
      const start = starts.get(before);
      // A block that isn't drawn on its own, as in a table, has no place.
      if (!start && (before <= last || !end)) return;

      const slot = doc.createElement("p");
      slot.className = SETUP_SLOT_CLASS;
      slot.dataset.wfSetup = String(block);
      slot.setAttribute("role", "button");
      slot.tabIndex = 0;
      if (start) start.before(slot);
      else end?.after(slot);
      placed.set(block, slot);
    });
    return () => {
      placed.forEach((slot) => slot.remove());
      placed.clear();
    };
  }, [root, starts, places]);

  useLayoutEffect(() => {
    slots.forEach(({ block, name, label }) => {
      const slot = drawn.current.get(block);
      if (!slot || slot.textContent === label) return;
      slot.textContent = label;
      slot.setAttribute("aria-label", `${name}, show in list`);
    });
  }, [slots, places, starts]);

  useLayoutEffect(() => {
    drawn.current.forEach((slot, block) =>
      slot.classList.toggle(SELECTED_CLASS, block === selected),
    );
  }, [selected, places, starts]);

  // A section picked in the list shows in the Report, unless it's in view.
  useEffect(() => {
    const view = frameRef.current?.contentWindow;
    const slot = root?.querySelector(`[data-wf-setup="${String(selected)}"]`);
    if (view && slot && !isInView(slot, view))
      slot.scrollIntoView({ block: "center" });
  }, [frameRef, root, selected]);

  const enabled = slots.length > 0;
  useEffect(() => {
    const doc = root?.ownerDocument;
    if (!enabled || !doc) return;
    const blockOf = (event: Event) =>
      (event.target as Element | null)
        ?.closest?.(`.${SETUP_SLOT_CLASS}`)
        ?.getAttribute("data-wf-setup");
    const click = (event: MouseEvent) => {
      const block = blockOf(event);
      if (block) onSelect(Number(block));
    };
    // Enter or Space on a slot, as a button takes them.
    const key = (event: KeyboardEvent) => {
      const block = blockOf(event);
      if (!block || (event.key !== "Enter" && event.key !== " ")) return;
      event.preventDefault();
      onSelect(Number(block));
    };
    doc.addEventListener("click", click);
    doc.addEventListener("keydown", key);
    return () => {
      doc.removeEventListener("click", click);
      doc.removeEventListener("keydown", key);
    };
  }, [root, enabled, onSelect]);
};
